/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { DimensionResults, Dimensions } from "@dojo/framework/core/meta/Dimensions";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { defaultsDeep } from "lodash";
import * as Constants from "../../Constants";
import { generateUUID, IShapeBox } from "../../store/reducers/util";
import { ICustomShapePoint } from "../interfaces/model/dataStructures/CustomShapePoint";
import { GeometryTypes } from "../interfaces/model/enumerations/GeometryTypes";
import { CanvasMode, IPageObjectTypes, Keys, LineCap } from "../interfaces/model/enumerations/index";
import { ICustomGeometry } from "../interfaces/model/geometry/CustomGeometry";
import { ITransform2D } from "../interfaces/model/properties/index";
import { ISnapSvg } from "../interfaces/model/SnapSvg";
import PageHelper from "../../store/PageHelper";
import { IPosition } from "../meta/Drag/CsiDragController";
import { getCustomGeometry } from "../selectors/geometry";
import { getSnapValue } from "../selectors/index";
import { getZoomPosition } from "../selectors/stateAwareConstants";
import * as util from "../util";
import { ICreateShapeRenderProps } from "./CanvasCreateShape";
import { MarkerObject } from "./MarkerObject";

// declaring types for snapsvg
declare const Snap: ISnapSvg;

/**
 * Canvas Draw custom shape.
 * Here we use optimistic rendering, a shape is rendered before adding to the state.
 * This way user can see shape before it get's added, as well it will get added in single operation.
 */
export class CreateCustomShape extends WidgetBase<ICreateShapeRenderProps> {
	/**
     * Marker head id
     */
	private markerHeadID = "";

	/**
     * Marker tail id
     */
	private markerTailID = "";

	/**
     * Key for drawing board.
     * @type {string}
     */
	private key = "drawingBoard";

	/**
     * Key for the shape being drawn, required to retrieve dimensions for the shape.
     * @type {string}
     */
	private shapeKey = Constants.TEMP_SHAPE;

	/**
     * Store dimensions for the drawing board.
     * @type {DimensionResults}
     */
	private drawingBoardDimensions: DimensionResults | undefined = undefined;

	/**
     * Array of shape points, used for drawing shape.
     * @type {ICustomShapePoint[]}
     */
	private shapePoints: ICustomShapePoint[] = [];

	/**
     * Custom shape defaults props
     */
	private shapeDefaultProps: { [key: string]: any } = {};

	/**
     * Target page index for which shape creation started.
     * This is useful to guard shape creation against page selection change when in draw mode.
     */
	private targetPageId: string | undefined = undefined;

	/**
     * Keep the instance of Timer returned by setTimeout, used to clear timeout
     */
	private timer: any;

	/**
     * Mouse click counter, needs to handle multiple clicks for IE 11
     */
	private clickCount = 0;

	/**
     * Holds event handlers registered for this widget. When widget is detached, these handlers will be removed.
     */
	private _boundHandlers: any[] = [];

	/**
     * Handle to the Timer on Touch Start Event
     * @type {any}
     */
	private tapHoldTimerHandle: any = null;

	constructor() {
		super();
		this._boundHandlers = [];

		// Add event listeners for create custom shape
		[
			{ event: "pointermove", func: this._onPointerMove.bind(this) },
			{ event: "keydown", func: this._onKeyDown.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event as any, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
     * Remove all event listener
     */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
	}

	/**
     * Key down event on document
     * @param event {KeyboardEvent}
     */
	protected _onKeyDown(event: KeyboardEvent) {
		const isEscapeKey = event.keyCode === Keys.ESCAPE;
		const isEnterKey = event.keyCode === Keys.ENTER;
		if (isEnterKey || isEscapeKey) {
			this.shapePoints.pop();
			this.addCurveOnPage(isEscapeKey);
		}
	}

	/**
     * Pointer Up event for shape
     * @param event {PointerEvent}
     */
	protected _onPointerDown(event: PointerEvent) {
		const point = {
			x: event.clientX,
			y: event.clientY,
		};

		// In IE11, event.detail always gives zero, no matter how many times mouse get clicked.
		if (event.detail && event.detail === 1) {
			this._addPoint(point);
		} else if (event.detail === 0) {
			this.clickCount++;

			if (this.clickCount === 1) {
				this.timer = setTimeout(() => {
					if (this.clickCount === 1) {
						this._addPoint(point);
					}

					this.clickCount = 0;
				}, Constants.IE_DOUBLE_CLICK_DELAY);
			}
		}
	}

	/**
     * On double click we complete the shape and call addPageObject.
     * @param {MouseEvent} event
     */
	protected _onMouseDblClick(event: MouseEvent) {
		// In IE11, event.detail always gives zero, no matter how many times mouse get clicked.
		if (event.detail === 0) {
			clearTimeout(this.timer);
		}
		const point = {
			x: event.clientX,
			y: event.clientY,
		};

		this._addPoint(point);
		this.addCurveOnPage();
	}

	/**
     * Add drawn curve to actual page.
     * @param editOnComplete {boolean | undefined}
     */
	protected addCurveOnPage(editOnComplete?: boolean) {
		const shapeToAdd = this._completeShape();
		if (shapeToAdd) {
			const { api } = this.properties;

			// Add shape to actual pageObjects
			api.pageApi.addPageObject(shapeToAdd);

			// Reset canvas mode to select or edit mode depending on key pressed or not
			if (editOnComplete) {
				api.canvasApi.editPageObject();
			} else {
				api.canvasApi.setCanvasMode(CanvasMode.SELECT, true);
			}
		}

		// Reset shape draw tools on shape draw complete
		this.properties.api.selectionApi.setShapeDrawTool();
		this.shapePoints = [];
	}

	/**
     * On point move on drawing add new point to the shape.
     * @param {PointerEvent} event
     */
	protected _onPointerMove(event: PointerEvent) {
		this.drawingBoardDimensions = this.meta(Dimensions)
			.get(this.key);
		// only if there is already a point present
		if (this.shapePoints.length) {
			// get X,Y location for click
			const x = getSnapValue(event.clientX - this.drawingBoardDimensions.position.left);
			const y = getSnapValue(event.clientY - this.drawingBoardDimensions.position.top);

			const positionWithinEditor = util.adjustPositionWithinEditor({ x, y }, this.properties.api.canvasContainerEl.getBoundingClientRect(), true, true);

			if (this.shapePoints.length > 1) {
				this.shapePoints.pop();
			}

			// apply zoom factor
			const position = getZoomPosition(positionWithinEditor);
			const shapePoint: ICustomShapePoint = {
				isCurvePoint: true,
				position,
			};
			this.shapePoints.push(shapePoint);

			this.invalidate();
		}
	}

	/**
     * Timer to detect Tap Hold
     * @param {number} delay
     */
	protected tapHoldTimer(delay: number) {
		this.tapHoldTimerHandle = setTimeout(() => {
			const mouseEvent: any = {};
			this._onMouseDblClick(mouseEvent);
		}, delay);
	}

	/**
     * Override WidgetBase#render to produce a virtual DOM tree.
     * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
     */
	protected render() {
		if (this.targetPageId !== this.properties.page.id) {
			this.shapePoints = [];
		}
		this.drawingBoardDimensions = this.meta(Dimensions)
			.get(this.key);
		return (
			<div
				classes="c8_canvas_modeDraw"
				key={this.key}
				ondblclick={this._onMouseDblClick}
				onpointerdown={this._onPointerDown}
				ontouchend={() => { clearTimeout(this.tapHoldTimerHandle); }}
				ontouchmove={() => { clearTimeout(this.tapHoldTimerHandle); }}
				ontouchstart={() => { this.tapHoldTimer(1000); }}
				styles={{ overflow: "visible", zIndex: `${Constants.MAX_Z_INDEX}` }}
				touch-action="auto"
			>
				{this.renderShape()}
			</div>
		);
	}

	/**
     * Add marker if line has headEnd and headTail properties
     * @return {MarkerObject | null}
     */
	protected renderDefs(): DNode {
		if (this.shapeDefaultProps.spPr.ln) {
			return (
				<MarkerObject
					lineProps={this.shapeDefaultProps.spPr.ln}
					markerHeadID={this.markerHeadID}
					markerTailID={this.markerTailID}
				/>
			);
		}
		return null;
	}

	/**
     * Render shape to be drawn.
     * @returns {DNode}
     */
	private renderShape() {
		// If more than 1 points added
		if (this.shapePoints.length > 1) {
			const operations = util.generateShapePathList(this.shapePoints, true);

			// create shape geometry
			const shapeGeometry: ICustomGeometry = {
				// add MoveTo path operation
				pathLst: [{
					operations,
				}],
				type: GeometryTypes.CUSTOM,
			};

			// user defaults
			const stroke = this.shapeDefaultProps.spPr.ln.fill.color;
			const strokeWidth = this.shapeDefaultProps.spPr.ln.W;
			const dash = this.shapeDefaultProps.spPr.ln.dash;
			// generate geometry for the shape.
			const geometryParams = getCustomGeometry({
				geometry: shapeGeometry,
				shapeAvList: undefined,
			});
			return (
				<svg
					height={`${Constants.PERCENTAGE_HUNDRED}%`}
					styles={{ overflow: "visible" }}
					width={`${Constants.PERCENTAGE_HUNDRED}%`}
					x="0"
					y="0"
				>
					{this.renderDefs()}
					<g>
						<path
							d={`${geometryParams.pathInfo.pathString}`}
							fill="none"
							key={`${this.shapeKey}`}
							marker-end={this.markerTailID && `url(#${this.markerTailID})`}
							marker-start={this.markerHeadID && `url(#${this.markerHeadID})`}
							stroke={`${stroke}`}
							stroke-dasharray={`${Constants.STROKE_DASH_MAPPING[dash]}`}
							stroke-width={`${strokeWidth}`}
							vector-effect="non-scaling-stroke"
						/>
					</g>
				</svg>
			);
		}
		return null;
	}

	/**
     * Adds point to custom shape path.
     * @param {IPosition} point the point to be added to shape path
     */
	private _addPoint(point: IPosition) {
		this.shapeDefaultProps.spPr = {};
		this.drawingBoardDimensions = this.meta(Dimensions)
			.get(this.key);
		const shapeDefaults = this.properties.selection.defaultShapeProperties as { [key: string]: any };
		let lineProps = shapeDefaults && shapeDefaults.ln;
		let fill = shapeDefaults && shapeDefaults.spPr.fill;
		// Merge current properties with shape defaults properties
		lineProps = defaultsDeep(lineProps, Constants.DEFAULT_SHAPE_PROPERTIES.spPr.ln,
			lineProps);
		fill = defaultsDeep(fill, Constants.DEFAULT_SHAPE_PROPERTIES.spPr.fill);
		this.shapeDefaultProps.spPr.fill = fill;
		this.shapeDefaultProps.spPr.ln = lineProps;

		// Default arrow head
		const { headEnd, tailEnd } = this.shapeDefaultProps.spPr.ln;
		const isHead = (headEnd && headEnd !== LineCap.NO_ARROW);
		const isTail = (tailEnd && tailEnd !== LineCap.NO_ARROW);
		this.markerHeadID = isHead ? `${generateUUID()}_headID` : "";
		this.markerTailID = isTail ? `${generateUUID()}_tailID` : "";

		this.targetPageId = this.properties.page.id;
		// get X,Y location for click
		const x = point.x - this.drawingBoardDimensions.position.left;
		const y = point.y - this.drawingBoardDimensions.position.top;
		// apply zoom factor
		const position = getZoomPosition({ x, y });
		const shapePoint: ICustomShapePoint = {
			isCurvePoint: true,
			position,
		};
		this.shapePoints.push(shapePoint);
		this.invalidate();
	}

	/**
     * Shape drawing is complete, now we need to scale, translate shape as per shape bounds.
     */
	private _completeShape() {
		// Get dimensions for the drawn shape,
		// we need to update offset and scale shape for 10 inches in EMU (english metric unit)

		if (this.shapePoints.length > 1) {
			const operations = util.generateShapePathList(this.shapePoints);

			// create shape geometry
			const shapeGeometry: ICustomGeometry = {
				// add MoveTo path operation
				pathLst: [{
					operations,
				}],
				type: GeometryTypes.CUSTOM,
			};
			// generate geometry for the shape.
			const geometryParams = getCustomGeometry({
				geometry: shapeGeometry,
				shapeAvList: undefined,
			});
			const pathString = geometryParams.pathInfo.pathString;
			// SnapSvg path.getBBox method gives path box by providing path string,
			// also this library method supports for all modern browsers.
			const curveBox: IShapeBox = Snap.path.getBBox(pathString);
			// curve shape box
			const left = curveBox.x;
			const top = curveBox.y;
			const { height, width } = curveBox;

			const tempShape = {
				allowsArrows: true,
				shapeId: generateUUID(),
				spPr: {
					geometry: shapeGeometry,
					ln: {
						W: this.shapeDefaultProps.spPr.ln.W,
						dash: this.shapeDefaultProps.spPr.ln.dash,
						fill: {
							color: this.shapeDefaultProps.spPr.ln.fill.color,
						},
						headEnd: this.shapeDefaultProps.spPr.ln.headEnd,
						tailEnd: this.shapeDefaultProps.spPr.ln.tailEnd,
					},
					xfrm: {
						ext: { cx: 0, cy: 0 },
						flipH: false,
						flipV: false,
						off: {
							x: 0,
							y: 0,
						},
						rot: 0,
					},
				},
				type: IPageObjectTypes.SHAPE,
			};
			const xfrm: ITransform2D = tempShape.spPr.xfrm;

			// set shape dimensions.
			xfrm.ext.cx = width;
			xfrm.ext.cy = height;

			// set offsets to shape.
			const { canvasContainerEl } = this.properties.api;
			util.adjustPositionForRoot(canvasContainerEl, xfrm.off, { x: left, y: top });

			// we need to adjust offsets & scale each point from all path operation list
			const pathLst = tempShape.spPr.geometry.pathLst;
			const pathOperationLst = pathLst && pathLst[0] && pathLst[0].operations;

			if (pathLst && pathOperationLst) {
				// Here we are using 914400 (10 inches in EMU (english metric unit) ) as out computation basis.
				// Shape is drawn based on this value.
				util.updateShapePathList(pathLst, -1 * left, -1 * top);
			}
			return tempShape;
		}
		return;
	}
}

export default CreateCustomShape;
